export type PortalRiskLevel = 'high' | 'medium' | 'low';

export type PortalRiskSituation = 'treatment' | 'monitoring' | 'controlled';

export type PortalChangeType = 'improvement' | 'scope' | 'fix';

export type PortalChangeSituation = 'analysis' | 'awaitingApproval' | 'approved' | 'implemented';

export type PortalRiskItem = {
  id: string;
  title: string;
  description: string;
  probability: PortalRiskLevel;
  impact: PortalRiskLevel;
  situation: PortalRiskSituation;
  ownerName: string;
  cause: string;
  responsePlan: string;
  impactSchedule: string;
  impactScope: string;
  impactCost: string;
  history: { id: string; title: string; detail: string; whenLabel: string }[];
};

export type PortalChangeItem = {
  id: string;
  number: string;
  title: string;
  description: string;
  type: PortalChangeType;
  priorityLabel: string;
  situation: PortalChangeSituation;
  dateLabel: string;
  requestedBy: string;
  ownerName: string;
  impactSchedule: string;
  impactScope: string;
  impactCost: string;
  justification: string;
  approvals: { id: string; name: string; role: string; statusLabel: string }[];
  history: { id: string; title: string; detail: string; whenLabel: string }[];
};

export type PortalRisksChangesData = {
  projectId: string;
  risksInAttention: number;
  changesInAnalysis: number;
  changesApproved: number;
  risks: PortalRiskItem[];
  changes: PortalChangeItem[];
};

export const portalRisksChangesByProjectId: Record<string, PortalRisksChangesData> = {
  crm: {
    projectId: 'crm',
    risksInAttention: 3,
    changesInAnalysis: 2,
    changesApproved: 5,
    risks: [
      {
        id: 'risk-api',
        title: 'Atraso na liberação da API do CRM',
        description: 'Dependência externa pode impactar a integração com o ERP.',
        probability: 'high',
        impact: 'high',
        situation: 'treatment',
        ownerName: 'Rafael Oliveira',
        cause: 'Fila do fornecedor da API e janela limitada de homologação.',
        responsePlan:
          'Priorizar endpoints críticos, acompanhar SLA semanal e preparar mock temporário para testes internos.',
        impactSchedule: '+ 10 dias',
        impactScope: 'Integração com ERP',
        impactCost: 'Não estimado',
        history: [
          {
            id: 'rh1',
            title: 'Risco elevado para tratamento',
            detail: 'Probabilidade reavaliada como Alta',
            whenLabel: '22/10/2026',
          },
        ],
      },
      {
        id: 'risk-training',
        title: 'Baixa adesão ao treinamento',
        description: 'Agenda do time comercial pode reduzir a participação nas sessões.',
        probability: 'medium',
        impact: 'medium',
        situation: 'monitoring',
        ownerName: 'Ana Souza',
        cause: 'Conflito com campanha comercial do trimestre.',
        responsePlan: 'Oferecer turmas extras e gravações sob demanda.',
        impactSchedule: '+ 3 dias',
        impactScope: 'Capacitação',
        impactCost: 'R$ 2.000',
        history: [],
      },
      {
        id: 'risk-data',
        title: 'Qualidade da base de clientes',
        description: 'Cadastros duplicados podem gerar retrabalho na importação.',
        probability: 'medium',
        impact: 'high',
        situation: 'treatment',
        ownerName: 'Carlos Mendes',
        cause: 'Fontes distintas sem deduplicação prévia.',
        responsePlan: 'Rodar limpeza assistida antes da carga definitiva.',
        impactSchedule: '+ 5 dias',
        impactScope: 'Importação de base',
        impactCost: 'R$ 4.500',
        history: [],
      },
      {
        id: 'risk-access',
        title: 'Atraso na liberação de acessos',
        description: 'Perfis de homologação dependem da TI do cliente.',
        probability: 'low',
        impact: 'medium',
        situation: 'monitoring',
        ownerName: 'Mariana Silva',
        cause: 'Fila interna de segurança da informação.',
        responsePlan: 'Escalar patrocinador se prazo ultrapassar 48h.',
        impactSchedule: '+ 2 dias',
        impactScope: 'Homologação',
        impactCost: 'Não estimado',
        history: [],
      },
      {
        id: 'risk-infra',
        title: 'Instabilidade do ambiente de testes',
        description: 'Intermitência pontual no ambiente de homologação.',
        probability: 'low',
        impact: 'low',
        situation: 'controlled',
        ownerName: 'Bruno Costa',
        cause: 'Janela de manutenção não comunicada.',
        responsePlan: 'Monitoramento contínuo e canal direto com infra.',
        impactSchedule: 'Sem impacto',
        impactScope: 'Ambiente de testes',
        impactCost: 'R$ 0',
        history: [
          {
            id: 'rh2',
            title: 'Risco controlado',
            detail: 'Ambiente estabilizado após ajuste de rede',
            whenLabel: '18/10/2026',
          },
        ],
      },
    ],
    changes: [
      {
        id: 'chg-015',
        number: '015',
        title: 'Incluir novo indicador no dashboard',
        description: 'Adicionar KPI de conversão por etapa no painel gerencial.',
        type: 'improvement',
        priorityLabel: 'Alta',
        situation: 'analysis',
        dateLabel: '20/10/2026',
        requestedBy: 'Ana Souza',
        ownerName: 'Rafael Oliveira',
        impactSchedule: '+ 5 dias',
        impactScope: 'Dashboard gerencial',
        impactCost: 'R$ 8.000',
        justification: 'O time comercial precisa acompanhar a conversão por etapa em tempo real.',
        approvals: [
          { id: 'a1', name: 'João Santos', role: 'Patrocinador', statusLabel: 'Pendente' },
          { id: 'a2', name: 'Mariana Silva', role: 'Gerente do projeto', statusLabel: 'Em análise' },
        ],
        history: [
          {
            id: 'ch1',
            title: 'Solicitação aberta',
            detail: 'Registrada por Ana Souza',
            whenLabel: '20/10/2026',
          },
        ],
      },
      {
        id: 'chg-014',
        number: '014',
        title: 'Ampliar escopo da integração de estoque',
        description: 'Incluir saldos por depósito na sincronização com o ERP.',
        type: 'scope',
        priorityLabel: 'Alta',
        situation: 'awaitingApproval',
        dateLabel: '18/10/2026',
        requestedBy: 'João Santos',
        ownerName: 'Carlos Mendes',
        impactSchedule: '+ 8 dias',
        impactScope: 'Integração ERP',
        impactCost: 'R$ 12.000',
        justification: 'Operação precisa do saldo por depósito para liberar pedidos.',
        approvals: [
          { id: 'a1', name: 'João Santos', role: 'Patrocinador', statusLabel: 'Aguardando' },
        ],
        history: [],
      },
      {
        id: 'chg-013',
        number: '013',
        title: 'Corrigir validação de CPF no cadastro',
        description: 'Ajuste na regra de validação que bloqueia cadastros válidos.',
        type: 'fix',
        priorityLabel: 'Média',
        situation: 'approved',
        dateLabel: '12/10/2026',
        requestedBy: 'Fernanda Lima',
        ownerName: 'Rafael Oliveira',
        impactSchedule: '+ 1 dia',
        impactScope: 'Cadastro de contatos',
        impactCost: 'R$ 1.500',
        justification: 'Erro impede o cadastro de alguns clientes ativos.',
        approvals: [
          { id: 'a1', name: 'João Santos', role: 'Patrocinador', statusLabel: 'Aprovada' },
        ],
        history: [],
      },
      {
        id: 'chg-012',
        number: '012',
        title: 'Adiar go-live em 1 semana',
        description: 'Replanejamento para acomodar validação do cliente.',
        type: 'scope',
        priorityLabel: 'Alta',
        situation: 'implemented',
        dateLabel: '05/10/2026',
        requestedBy: 'Mariana Silva',
        ownerName: 'Mariana Silva',
        impactSchedule: '+ 7 dias',
        impactScope: 'Cronograma geral',
        impactCost: 'R$ 0',
        justification: 'Necessário para concluir a homologação com qualidade.',
        approvals: [],
        history: [],
      },
      {
        id: 'chg-011',
        number: '011',
        title: 'Incluir campo de prioridade na oportunidade',
        description: 'Campo adicional no funil comercial.',
        type: 'improvement',
        priorityLabel: 'Baixa',
        situation: 'approved',
        dateLabel: '28/09/2026',
        requestedBy: 'Ricardo Alves',
        ownerName: 'Ana Souza',
        impactSchedule: '+ 2 dias',
        impactScope: 'Pipeline comercial',
        impactCost: 'R$ 3.000',
        justification: 'Facilita a triagem diária do time de vendas.',
        approvals: [],
        history: [],
      },
      {
        id: 'chg-010',
        number: '010',
        title: 'Ajuste no fluxo de aprovação',
        description: 'Nova regra de alçada para pedidos acima do limite.',
        type: 'improvement',
        priorityLabel: 'Média',
        situation: 'implemented',
        dateLabel: '20/09/2026',
        requestedBy: 'João Santos',
        ownerName: 'Carlos Mendes',
        impactSchedule: '+ 4 dias',
        impactScope: 'Aprovações',
        impactCost: 'R$ 5.000',
        justification: 'Alinhamento com política comercial da empresa.',
        approvals: [],
        history: [],
      },
      {
        id: 'chg-009',
        number: '009',
        title: 'Correção do filtro de filiais',
        description: 'Filtro não aplicava corretamente a regional selecionada.',
        type: 'fix',
        priorityLabel: 'Média',
        situation: 'implemented',
        dateLabel: '15/09/2026',
        requestedBy: 'Fernanda Lima',
        ownerName: 'Rafael Oliveira',
        impactSchedule: '+ 1 dia',
        impactScope: 'Listagens',
        impactCost: 'R$ 800',
        justification: 'Impedia a operação regional no piloto.',
        approvals: [],
        history: [],
      },
    ],
  },
};
